import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CalendarClock, Send, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

const WEEKDAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const LANGUAGES = ["pt", "es", "en", "ar"];
const LANG_LABELS = { pt: "Português", es: "Español", en: "English", ar: "العربية" };

function nextScheduledDate(weekday, time) {
  const days = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const targetDay = days.indexOf(weekday);
  if (targetDay === -1 || !time) return null;
  const now = new Date();
  const [h, m] = time.split(":").map(Number);
  const result = new Date(now);
  result.setHours(h, m, 0, 0);
  const diff = (targetDay - now.getDay() + 7) % 7;
  result.setDate(now.getDate() + (diff === 0 && result <= now ? 7 : diff));
  return result.toLocaleDateString(undefined, { weekday: "long", day: "2-digit", month: "long" }) + " " + time;
}

export function WeeklyReportModal({ open, onClose }) {
  const { t } = useTranslation();
  const { authAxios } = useAuth();
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [testing, setTesting]     = useState(false);
  const [settings, setSettings]   = useState({
    enabled: false,
    weekday: "monday",
    time: "09:00",
    timezone: "Europe/Madrid",
    owner_email: "",
    cc_emails: [],
    language: "pt",
  });
  const [ccRaw, setCcRaw] = useState("");

  // Load settings when modal opens
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    authAxios.get("/financiero/weekly-report-settings")
      .then(r => {
        const s = r.data;
        setSettings(s);
        setCcRaw((s.cc_emails || []).join(", "));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, authAxios]);

  const set = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    const cc = ccRaw.split(",").map(e => e.trim()).filter(Boolean);
    try {
      await authAxios.put("/financiero/weekly-report-settings", { ...settings, cc_emails: cc });
      setSettings(prev => ({ ...prev, cc_emails: cc }));
      toast.success(t("iaFin.saved"));
    } catch {
      toast.error(t("common.error", "Erro ao salvar"));
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    const cc = ccRaw.split(",").map(e => e.trim()).filter(Boolean);
    try {
      await authAxios.post("/financiero/weekly-report-settings/send-test", { ...settings, cc_emails: cc });
      toast.success(t("iaFin.sendTestSuccess"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("iaFin.sendTestError"));
    }
    setTesting(false);
  };

  const nextSend = settings.enabled ? nextScheduledDate(settings.weekday, settings.time) : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4 text-[#CDA34F]" />
            {t("iaFin.weeklyReportTitle")}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <div className="space-y-4 py-1">
            {/* Enable toggle */}
            <div className="flex items-center justify-between py-1 border-b">
              <div>
                <Label className="font-semibold">{t("iaFin.enableAutoSend")}</Label>
                <p className="text-xs text-gray-500 mt-0.5">{t("iaFin.frequency")}: {t("iaFin.weekly")}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold ${settings.enabled ? "text-emerald-600" : "text-gray-400"}`}>
                  {settings.enabled ? t("iaFin.statusActive") : t("iaFin.statusInactive")}
                </span>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={v => set("enabled", v)}
                  data-testid="weekly-report-toggle"
                />
              </div>
            </div>

            {/* Day + Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-gray-600">{t("iaFin.dayOfWeek")}</Label>
                <Select value={settings.weekday} onValueChange={v => set("weekday", v)}>
                  <SelectTrigger className="mt-1 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {WEEKDAYS.map(d => (
                      <SelectItem key={d} value={d}>{t(`iaFin.days.${d}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">{t("iaFin.sendTime")}</Label>
                <Input
                  type="time"
                  className="mt-1 text-sm"
                  value={settings.time}
                  onChange={e => set("time", e.target.value)}
                />
              </div>
            </div>

            {/* Owner email */}
            <div>
              <Label className="text-xs font-semibold text-gray-600">{t("iaFin.ownerEmail")}</Label>
              <Input
                type="email"
                className="mt-1 text-sm"
                value={settings.owner_email}
                onChange={e => set("owner_email", e.target.value)}
                placeholder="owner@restaurant.com"
              />
            </div>

            {/* Language */}
            <div>
              <Label className="text-xs font-semibold text-gray-600">{t("iaFin.reportLanguage")}</Label>
              <Select value={settings.language} onValueChange={v => set("language", v)}>
                <SelectTrigger className="mt-1 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">{t("iaFin.followAccount")}</SelectItem>
                  {LANGUAGES.map(l => (
                    <SelectItem key={l} value={l}>{LANG_LABELS[l]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* CC emails */}
            <div>
              <Label className="text-xs font-semibold text-gray-600">{t("iaFin.ccEmails")}</Label>
              <Input
                type="text"
                className="mt-1 text-sm"
                value={ccRaw}
                onChange={e => setCcRaw(e.target.value)}
                placeholder={t("iaFin.ccEmailsPlaceholder")}
              />
            </div>

            {/* Next send info */}
            {nextSend && (
              <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                <Clock className="w-4 h-4 text-emerald-600 shrink-0" />
                <p className="text-xs text-emerald-700">
                  <span className="font-semibold">{t("iaFin.nextScheduled")}:</span> {nextSend}
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={handleTest}
            disabled={testing || !settings.owner_email}
            data-testid="send-test-btn"
          >
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            {t("iaFin.sendTest")}
          </Button>
          <Button
            size="sm"
            className="bg-[#CDA34F] hover:bg-[#b8903e] gap-1.5"
            onClick={handleSave}
            disabled={saving || loading}
            data-testid="save-weekly-report-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            {t("iaFin.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
